import { Presupuesto, Traspaso, TraspasoRecurrente } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Agrupación de presupuestos recurrentes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Clave estable que identifica el "grupo" lógico de un presupuesto recurrente.
 * Usa grupoId si existe; si no (datos aún sin migrar), cae a una clave compuesta
 * por categoría + periodicidad + nombre, equivalente a la agrupación histórica.
 * Así las lecturas funcionan igual antes y después de la migración de grupoId.
 */
export function claveGrupoPresupuesto(
  p: Pick<Presupuesto, 'grupoId' | 'categoriaId' | 'periodicidad' | 'nombre'>,
): string {
  return p.grupoId ?? `${p.categoriaId}|${p.periodicidad}|${p.nombre}`;
}

/**
 * Devuelve una función que, dado un presupuestoId, resuelve la clave de grupo del
 * presupuesto al que apunta. Pensada para imputar transacciones a su presupuesto
 * por grupo en lugar de por id literal (que varía entre meses).
 */
export function crearResolverGrupo(
  presupuestos: Presupuesto[],
): (presupuestoId?: string | null) => string | undefined {
  const porId = new Map(presupuestos.map(p => [p.id, claveGrupoPresupuesto(p)]));
  return (presupuestoId?: string | null) =>
    presupuestoId ? porId.get(presupuestoId) : undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ─────────────────────────────────────────────────────────────────────────────

function diasEnMes(mes: number, anio: number): number {
  // mes 1-based
  return new Date(anio, mes, 0).getDate();
}

/**
 * Período al que pertenece una fecha según la periodicidad del presupuesto:
 * 'YYYY-MM' para mensuales, 'YYYY' para anuales. Mismo formato que Presupuesto.mes,
 * apto para comparación lexicográfica.
 */
export function periodoDeFecha(fecha: Date, periodicidad: 'mensual' | 'anual'): string {
  const anio = fecha.getFullYear();
  if (periodicidad === 'anual') return String(anio);
  return `${anio}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * ¿Aplica este presupuesto al período indicado? Fuente de verdad única compartida
 * por la página de Presupuestos y por el selector del modal de transacciones, para
 * que ambos muestren siempre el mismo conjunto.
 *  - Repetitivo: activo desde su inception (p.mes) en adelante  → p.mes <= periodo
 *  - Puntual (no repetitivo): solo en su período exacto          → p.mes === periodo
 * periodo: 'YYYY-MM' para mensuales, 'YYYY' para anuales (comparación lexicográfica).
 */
export function presupuestoActivoEnPeriodo(
  p: Pick<Presupuesto, 'repetitivo' | 'mes'>,
  periodo: string,
): boolean {
  return p.repetitivo ? p.mes <= periodo : p.mes === periodo;
}

// ─────────────────────────────────────────────────────────────────────────────
// Importe de un mes concreto
// ─────────────────────────────────────────────────────────────────────────────

type ConLimites = Pick<Presupuesto, 'limite' | 'periodicidad' | 'limitesMes' | 'historialLimite'>;

/** Importe por la regla general (tramos), sin las excepciones de un solo mes. */
function limiteSinExcepciones(p: ConLimites, mes: string): number {
  for (const tramo of p.historialLimite ?? []) {
    if (mes < tramo.hasta) return tramo.limite;
  }
  return p.limite;
}

/**
 * Importe de un presupuesto en un período. Fuente única para todo lo que compara
 * gasto con límite (Presupuestos, alertas del resumen, Informes, PDF).
 *  - Anual: siempre `limite` (los cambios por mes no existen en los anuales).
 *  - Mensual: la excepción de ese mes si la hay; si no, el tramo que le toca.
 */
export function limiteEnPeriodo(p: ConLimites, periodo: string): number {
  if (p.periodicidad !== 'mensual') return p.limite;
  const excepcion = p.limitesMes?.[periodo];
  return typeof excepcion === 'number' ? excepcion : limiteSinExcepciones(p, periodo);
}

/**
 * Suma de los importes de un presupuesto mensual repetitivo en los meses de un
 * año en que aplica (desde su inicio hasta diciembre), para compararlo con el
 * gasto del año entero. Mes a mes, así respeta los cambios de importe.
 */
export function limiteMensualEnAnio(p: ConLimites & Pick<Presupuesto, 'mes'>, anio: number): number {
  let total = 0;
  for (let m = 1; m <= 12; m++) {
    const periodo = `${anio}-${String(m).padStart(2, '0')}`;
    if (periodo >= p.mes) total += limiteEnPeriodo(p, periodo);
  }
  return total;
}

/** ¿Ese mes lleva un importe puesto solo para él? */
export function tieneExcepcionEnMes(p: ConLimites, periodo: string): boolean {
  return p.periodicidad === 'mensual' && typeof p.limitesMes?.[periodo] === 'number';
}

/**
 * Campos que hay que guardar para cambiar el importe de un presupuesto mensual
 * a partir de un mes. Nunca crea otro documento: así las transacciones siguen
 * apuntando al mismo presupuesto y no hay nada que duplicar ni migrar.
 *  - 'mes':   solo ese mes; el resto sigue igual.
 *  - 'desde': ese mes y los siguientes; los anteriores conservan su importe.
 * `inicio` es el mes de inicio del presupuesto (p.mes, ya con el valor nuevo
 * si también se ha cambiado): los tramos anteriores a él no sirven y se quitan.
 */
export function cambioDeLimite(
  p: ConLimites,
  inicio: string,
  periodo: string,
  importe: number,
  alcance: 'mes' | 'desde',
): { limite: number; limitesMes: Record<string, number>; historialLimite: { hasta: string; limite: number }[] } {
  let limite = p.limite;
  let historial = [...(p.historialLimite ?? [])];
  const excepciones: Record<string, number> = { ...(p.limitesMes ?? {}) };

  if (alcance === 'mes') {
    // Si coincide con lo que ya le tocaba, no hace falta excepción
    if (importe === limiteSinExcepciones(p, periodo)) delete excepciones[periodo];
    else excepciones[periodo] = importe;
  } else {
    // Lo que valía justo antes del mes del cambio se queda como tramo cerrado
    const tramosPrevios = historial.filter(t => t.hasta < periodo);
    if (periodo > inicio) {
      const [a, m] = periodo.split('-').map(Number);
      const anterior = `${m === 1 ? a - 1 : a}-${String(m === 1 ? 12 : m - 1).padStart(2, '0')}`;
      tramosPrevios.push({ hasta: periodo, limite: limiteSinExcepciones(p, anterior) });
    }
    historial = tramosPrevios;
    limite = importe;
    // «Desde este mes» manda sobre las excepciones de ese mes y los siguientes
    Object.keys(excepciones).forEach(k => { if (k >= periodo) delete excepciones[k]; });
  }

  // Limpieza: tramos que no cubren ningún mes del presupuesto, o que repiten
  // el importe del tramo siguiente, sobran
  historial = historial.filter(t => t.hasta > inicio);
  historial = historial.filter((t, i) => t.limite !== (historial[i + 1]?.limite ?? limite));
  Object.keys(excepciones).forEach(k => { if (k < inicio) delete excepciones[k]; });

  return { limite, limitesMes: excepciones, historialLimite: historial };
}

// ─────────────────────────────────────────────────────────────────────────────
// Importe mensual teórico de un presupuesto anual
// ─────────────────────────────────────────────────────────────────────────────

export function calcularImporteMensual(presupuesto: Presupuesto): number {
  if (presupuesto.periodicidad !== 'anual') return presupuesto.limite;
  return presupuesto.limite / 12;
}

// ─────────────────────────────────────────────────────────────────────────────
// Consumo / ahorro esperado a una fecha de referencia
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Consumo/ahorro esperado de un presupuesto para el PERÍODO que se está viendo en
 * la UI (periodoVista: 'YYYY-MM' para mensual, 'YYYY' para anual), NO para el
 * período de inicio del documento. fechaReferencia (hoy) solo sirve para saber
 * cuántos días/meses han transcurrido cuando el período visto es el actual.
 */
export function calcularConsumoEsperado(
  presupuesto: Presupuesto,
  periodoVista: string,
  fechaReferencia: Date = new Date()
): number {
  // Antes de la inception (mes de inicio del presupuesto) no aplica → esperado 0.
  // Comparación lexicográfica válida para 'YYYY-MM' y 'YYYY'.
  if (periodoVista < presupuesto.mes) return 0;

  if (presupuesto.periodicidad === 'mensual') {
    // Proporción de días transcurridos DEL MES VISTO
    const [anio, mes] = periodoVista.split('-').map(Number);
    const totalDias = diasEnMes(mes, anio);
    const hoy = new Date(fechaReferencia);
    const esElMismoMes =
      hoy.getFullYear() === anio && hoy.getMonth() + 1 === mes;
    const esFuturo =
      anio > hoy.getFullYear() || (anio === hoy.getFullYear() && mes > hoy.getMonth() + 1);
    const diasTranscurridos = esFuturo ? 0 : esElMismoMes ? hoy.getDate() : totalDias;
    return (limiteEnPeriodo(presupuesto, periodoVista) / totalDias) * diasTranscurridos;
  }

  // ── Presupuesto anual ────────────────────────────────────────────────────
  const añoVista = parseInt(periodoVista, 10);
  const añoRef = fechaReferencia.getFullYear();

  // Año visto futuro respecto a hoy: el ejercicio no ha comenzado → esperado = 0
  if (añoRef < añoVista) return 0;

  // Año visto cerrado respecto a hoy: el ejercicio está completo → esperado = límite anual
  if (añoRef > añoVista) return presupuesto.limite;

  // Año en curso (añoVista === añoRef): cálculo proporcional con fracción de días
  const importeMensual = presupuesto.limite / 12;
  const mesRef = fechaReferencia.getMonth() + 1; // 1–12
  const diaRef = fechaReferencia.getDate();
  const totalDiasMesRef = diasEnMes(mesRef, añoRef);
  const fraccionMesActual = (diaRef - 1) / totalDiasMesRef;

  const aplicaMesInicio =
    presupuesto.anioInicio !== undefined &&
    presupuesto.mesInicio !== undefined &&
    presupuesto.anioInicio === añoVista &&
    presupuesto.mesInicio > 1;

  let mesesTranscurridos: number;

  if (aplicaMesInicio) {
    const mesInicio = presupuesto.mesInicio as number;
    const mesesTeoricos = mesInicio - 1;
    if (mesRef < mesInicio) {
      mesesTranscurridos = mesesTeoricos;
    } else {
      mesesTranscurridos = mesesTeoricos + (mesRef - mesInicio) + fraccionMesActual;
    }
  } else {
    mesesTranscurridos = (mesRef - 1) + fraccionMesActual;
  }

  return importeMensual * mesesTranscurridos;
}

// ─────────────────────────────────────────────────────────────────────────────
// Acumulado total = consumo teórico previo + gastos reales + traspasos vinculados
// ─────────────────────────────────────────────────────────────────────────────

export function calcularAcumuladoTotal(
  presupuesto: Presupuesto,
  traspasos: (Traspaso | TraspasoRecurrente)[],
  añoVista: number,
): number {
  // Gasto real de transacciones + traspasos a inversión vinculados
  const acumuladoTraspasos = traspasos
    .filter((t) => t.presupuestoDestinoId === presupuesto.id)
    .reduce((sum, t) => sum + t.cantidad, 0);

  const gastoReal = presupuesto.gastado + acumuladoTraspasos;

  // Consumo teórico de meses previos al inicio (solo primer año y solo anuales).
  // Se suma para que el acumulado sea comparable con el esperado, que también lo
  // incluye. Se compara contra el AÑO VISTO (no el actual) para que el prorrateo
  // solo aplique cuando se está mirando el año en que arrancó el presupuesto.
  const aplicaMesInicio =
    presupuesto.periodicidad === 'anual' &&
    presupuesto.anioInicio !== undefined &&
    presupuesto.mesInicio !== undefined &&
    presupuesto.anioInicio === añoVista &&
    presupuesto.mesInicio > 1;

  if (aplicaMesInicio) {
    const importeMensual = presupuesto.limite / 12;
    const consumoTeoricoPrevio = (presupuesto.mesInicio as number - 1) * importeMensual;
    return gastoReal + consumoTeoricoPrevio;
  }

  return gastoReal;
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado del presupuesto respecto al objetivo
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoPresupuesto = 'adelantado' | 'en_objetivo' | 'retrasado' | 'no_iniciado' | 'completado';

export interface ResultadoEstadoPresupuesto {
  estado: EstadoPresupuesto;
  emoji: '🟢' | '🟡' | '🔴';
  label: string;
  diferencia: number;       // acumulado - esperado
  porcentaje: number;       // acumulado / esperado * 100 (0 si esperado === 0)
}

export function calcularEstadoPresupuesto(
  acumulado: number,
  esperado: number,
  esObjetivoAhorro: boolean,
  limite: number = 0,
  añoPresupuesto?: number,
  fechaReferencia: Date = new Date()
): ResultadoEstadoPresupuesto {
  const añoRef = fechaReferencia.getFullYear();

  // Año futuro — presupuesto no iniciado
  if (añoPresupuesto !== undefined && añoRef < añoPresupuesto) {
    return {
      estado: 'no_iniciado',
      emoji: '🟡',
      label: 'No iniciado',
      diferencia: 0,
      porcentaje: 0,
    };
  }

  // Año cerrado — ejercicio completo
  if (añoPresupuesto !== undefined && añoRef > añoPresupuesto) {
    const porcentaje = limite > 0 ? (acumulado / limite) * 100 : 0;
    const diferencia = acumulado - limite;
    const completado = esObjetivoAhorro ? acumulado >= limite : acumulado <= limite;
    return {
      estado: 'completado',
      emoji: completado ? '🟢' : '🔴',
      label: completado
        ? (esObjetivoAhorro ? 'Objetivo alcanzado' : 'Ejercicio bajo control')
        : (esObjetivoAhorro ? 'Objetivo no alcanzado' : 'Ejercicio excedido'),
      diferencia,
      porcentaje,
    };
  }

  // Año en curso — lógica normal
  const diferencia = acumulado - esperado;
  const umbral = esperado * 0.1;
  const porcentaje = esperado > 0 ? (acumulado / esperado) * 100 : 0;

  let estado: EstadoPresupuesto;

  if (esObjetivoAhorro) {
    if (diferencia >= umbral) estado = 'adelantado';
    else if (diferencia > -umbral) estado = 'en_objetivo';
    else estado = 'retrasado';
  } else {
    if (diferencia <= -umbral) estado = 'adelantado';
    else if (diferencia < umbral) estado = 'en_objetivo';
    else estado = 'retrasado';
  }

  const mapaEmoji: Record<EstadoPresupuesto, '🟢' | '🟡' | '🔴'> = {
    adelantado:  '🟢',
    en_objetivo: '🟡',
    retrasado:   '🔴',
    no_iniciado: '🟡',
    completado:  '🟢',
  };

  const mapaLabel: Record<EstadoPresupuesto, string> = {
    adelantado:  esObjetivoAhorro ? 'Adelantado' : 'Bajo control',
    en_objetivo: 'En objetivo',
    retrasado:   'Retrasado',
    no_iniciado: 'No iniciado',
    completado:  'Completado',
  };

  return {
    estado,
    emoji: mapaEmoji[estado],
    label: mapaLabel[estado],
    diferencia,
    porcentaje,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Resumen completo de un presupuesto anual (para uso en UI)
// ─────────────────────────────────────────────────────────────────────────────

export interface ResumenPresupuesto {
  importeMensual: number;
  esperado: number;
  acumulado: number;
  resultado: ResultadoEstadoPresupuesto;
}

export function calcularResumenPresupuesto(
  presupuesto: Presupuesto,
  traspasos: (Traspaso | TraspasoRecurrente)[],
  periodoVista: string, // 'YYYY-MM' para mensual, 'YYYY' para anual
  fechaReferencia: Date = new Date()
): ResumenPresupuesto {
  // Año del período visto: para anuales es el propio periodoVista; para mensuales,
  // el año contenido en el 'YYYY-MM'. Se usa en el prorrateo de mesInicio y en la
  // lógica de año futuro/cerrado del estado.
  const añoVista = presupuesto.periodicidad === 'anual'
    ? parseInt(periodoVista, 10)
    : parseInt(periodoVista.slice(0, 4), 10);
  const importeMensual = calcularImporteMensual(presupuesto);
  const esperado = calcularConsumoEsperado(presupuesto, periodoVista, fechaReferencia);
  const acumulado = calcularAcumuladoTotal(presupuesto, traspasos, añoVista);
  const añoPresupuesto = presupuesto.periodicidad === 'anual'
    ? añoVista
    : undefined;
  const resultado = calcularEstadoPresupuesto(
    acumulado,
    esperado,
    presupuesto.esObjetivoAhorro ?? false,
    presupuesto.limite,
    añoPresupuesto,
    fechaReferencia
  );

  return { importeMensual, esperado, acumulado, resultado };
}
